import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import api from '../mainApi';
import { getWorkoutTemplates, createWorkoutPlan, getAssignedWorkoutForMember } from '../workout-module/api'; // Import workoutApi functions

const Members = () => {
  const [members, setMembers] = useState([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [membershipPlan, setMembershipPlan] = useState('Monthly');
  const [planStartDate, setPlanStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentStatus, setPaymentStatus] = useState('Paid');
  const [personalTrainerId, setPersonalTrainerId] = useState(''); // New state for add form
  const [password, setPassword] = useState('');
  const [workoutPlanId, setWorkoutPlanId] = useState('');
  const [workoutTemplates, setWorkoutTemplates] = useState([]); // Renamed from workoutPlans

  const [showModal, setShowModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [attendanceData, setAttendanceData] = useState({});
  const [trainers, setTrainers] = useState([]); // New state for trainers

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch members (now includes workout_plan_id from backend join)
        const membersRes = await api.get('members');
        const membersData = membersRes.data;

        setMembers(membersData);
        setAttendanceData({}); // Initialize as empty if needed

        // Fetch trainers
        const trainersRes = await api.get('trainers');
        setTrainers(trainersRes.data);

        // Fetch workout templates from PHP backend
        const workoutTemplatesRes = await getWorkoutTemplates();
        setWorkoutTemplates(workoutTemplatesRes.data);

      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  const addMember = async (e) => {
    e.preventDefault();
    const newMember = {
      name,
      email,
      mobile_number: mobileNumber,
      membership_plan: membershipPlan,
      plan_start_date: planStartDate,
      payment_status: paymentStatus,
      personal_trainer_id: personalTrainerId === '' ? null : parseInt(personalTrainerId),
      password,
    };
    try {
      const res = await api.post('members', newMember);
      const newMemberData = res.data;
      
      if (workoutPlanId) {
        await createWorkoutPlan({ member_id: newMemberData.id, template_id: workoutPlanId });
        newMemberData.workout_plan_id = workoutPlanId;
      }
      setMembers([...members, newMemberData]);

      setName('');
      setEmail('');
      setMobileNumber('');
      setMembershipPlan('Monthly');
      setPlanStartDate(new Date().toISOString().split('T')[0]);
      setPaymentStatus('Paid');
      setPersonalTrainerId('');
      setPassword('');
      setWorkoutPlanId('');
    } catch (error) {
      console.error('Failed to add member', error);
    }
  };

  const deleteMember = (id) => {
    api.delete(`members/${id}`)
      .then(() => {
        setMembers(members.filter(member => member.id !== id));
      });
  };

  const sendReminders = () => {
    api.post('members/send-reminders')
      .then(res => {
        alert(res.data.message);
        console.log('Expiring members:', res.data.expiringMembers);
      });
  };

  const isExpiringSoon = (dateString) => {
    if (!dateString) return false;
    const expiryDate = new Date(dateString);
    const today = new Date();
    const tenDaysFromNow = new Date();
    tenDaysFromNow.setDate(today.getDate() + 10);
    return expiryDate >= today && expiryDate <= tenDaysFromNow;
  };

  const handleShowModal = async (member) => {
    try {
      // We already have workout_plan_id from the members list, 
      // but we can fetch it again to be sure if needed. 
      // For now, let's fix the logic to handle the array response.
      const res = await getAssignedWorkoutForMember(member.id);
      const assignedWorkout = res.data && res.data.length > 0 ? res.data[0] : null;
      
      setSelectedMember({
        ...member,
        name: member.name || '',
        email: member.email || '',
        membership_plan: member.membership_plan || 'Monthly',
        plan_start_date: member.plan_start_date ? new Date(member.plan_start_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        payment_status: member.payment_status || 'Paid',
        workout_plan_id: assignedWorkout ? assignedWorkout.template_id : (member.workout_plan_id || '')
      });
      setShowModal(true);
    } catch (error) {
      console.error('Failed to fetch assigned workout', error);
      setSelectedMember({
        ...member,
        name: member.name || '',
        email: member.email || '',
        membership_plan: member.membership_plan || 'Monthly',
        plan_start_date: member.plan_start_date ? new Date(member.plan_start_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        payment_status: member.payment_status || 'Paid',
        workout_plan_id: member.workout_plan_id || ''
      });
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMember(null);
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    try {
      const memberData = {
        name: selectedMember.name,
        email: selectedMember.email,
        mobile_number: selectedMember.mobile_number,
        membership_plan: selectedMember.membership_plan,
        plan_start_date: selectedMember.plan_start_date,
        payment_status: selectedMember.payment_status,
        personal_trainer_id: selectedMember.personal_trainer_id
      };

      const res = await api.put(`members/${selectedMember.id}`, memberData);
      const updatedMemberData = res.data;

      if (selectedMember.workout_plan_id) {
        await createWorkoutPlan({ member_id: selectedMember.id, template_id: selectedMember.workout_plan_id });
        updatedMemberData.workout_plan_id = selectedMember.workout_plan_id;
      }
      setMembers(members.map(member => member.id === updatedMemberData.id ? updatedMemberData : member));

      handleCloseModal();
    } catch (error) {
      console.error('Failed to update member', error);
    }
  };

  const getWorkoutPlanName = (planId) => {
    if (!planId || !workoutTemplates) return 'N/A';
    const plan = workoutTemplates.find(p => p.template_id == planId);
    return plan ? plan.template_name : 'N/A';
  };

  return (
    <div>
      <h2>Members</h2>
      <form onSubmit={addMember} autoComplete="off">
        <div className="row gy-2 mb-3">
          <div className="col-md-3"> {/* Changed from col-md-2 */}
            <input type="text" className="form-control" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="col-md-3"> {/* Changed from col-md-2 */}
            <input type="email" className="form-control" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="new-password" required />
          </div>
          <div className="col-md-3"> {/* Changed from col-md-2 */}
            <input type="text" className="form-control" placeholder="Mobile Number" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} autoComplete="off" />
          </div>
          <div className="col-md-3"> {/* Changed from col-md-2 */}
            <select className="form-select" value={membershipPlan} onChange={(e) => setMembershipPlan(e.target.value)}>
                <option value="Monthly">Monthly</option>
                <option value="Quatar">Quatar</option>
                <option value="Half">Half-Yearly</option>
                <option value="yearly">Yearly</option>
            </select>
          </div>
          <div className="col-md-3"> {/* Changed from col-md-2 */}
            <input type="date" className="form-control" value={planStartDate} onChange={(e) => setPlanStartDate(e.target.value)} required />
          </div>
          <div className="col-md-3"> {/* Changed from col-md-2 */}
            <select className="form-select" value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)}>
                <option value="Paid">Paid</option>
                <option value="Pending">Pending</option>
            </select>
          </div>
          <div className="col-md-3"> {/* Changed from col-md-2 */}
            <select className="form-select" value={personalTrainerId} onChange={(e) => setPersonalTrainerId(e.target.value)}>
                <option value="">No Personal Trainer</option>
                {trainers.map(trainer => (
                    <option key={trainer.id} value={trainer.id}>{trainer.name}</option>
                ))}
            </select>
          </div>
          <div className="col-md-3"><input type="password" className="form-control" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required /></div>
          <div className="col-md-3">
            <select className="form-select" value={workoutPlanId} onChange={(e) => setWorkoutPlanId(e.target.value)}>
                <option value="">No Workout Plan</option>
                {workoutTemplates.map(plan => (
                    <option key={plan.template_id} value={plan.template_id}>{plan.template_name}</option>
                ))}
            </select>
          </div>
          <div className="col-md-2 d-flex"> {/* Grouped buttons */}
            <button type="submit" className="btn btn-primary w-50 me-2">Add</button> {/* w-50 and me-2 for spacing */}
            <button type="button" className="btn btn-info w-50" onClick={sendReminders}>Reminders</button> {/* w-50 */}
          </div>
        </div>
      </form>

      <hr />

      <table className="table table-sm" style={{ whiteSpace: 'nowrap' }}>
        <thead>
          <tr>
            <th>ID</th>
            <th>Name</th>
            <th>Email</th>
            <th>Mobile</th> {/* Changed header */}
            <th>Plan</th> {/* Changed header */}
            <th>Start Date</th>
            <th>Expiry Date</th>
            <th>Payment</th> {/* Changed header */}
            <th>Attendance %</th>
            <th>Trainer</th> {/* Changed header */}
            <th>Workout Plan</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {members.map(member => (
            <tr key={member.id}>
              <td>{member.id}</td>
              <td>{member.name}</td>
              <td>{member.email}</td>
              <td>{member.mobile_number}</td>
              <td>{member.membership_plan}</td>
              <td>{member.plan_start_date ? new Date(member.plan_start_date).toLocaleDateString() : ''}</td>
              <td className={isExpiringSoon(member.plan_expiry_date) ? 'text-danger fw-bold' : ''}>
                {member.plan_expiry_date ? new Date(member.plan_expiry_date).toLocaleDateString() : ''}</td>
              <td>{member.payment_status}</td>
              <td>{attendanceData[member.id] !== undefined ? `${attendanceData[member.id]}%` : 'N/A'}</td>
              <td>{member.personal_trainer_name || 'N/A'}</td>
              <td>{getWorkoutPlanName(member.workout_plan_id)}</td>
              <td>
                <button className="btn btn-primary btn-sm me-2" onClick={() => handleShowModal(member)}>Edit</button>
                <button className="btn btn-danger btn-sm" onClick={() => deleteMember(member.id)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {selectedMember && (
        <Modal show={showModal} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Member</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={handleUpdateMember}>
              <div className="mb-3">
                <label htmlFor="name" className="form-label">Name</label>
                <input type="text" className="form-control" id="name" value={selectedMember.name} onChange={(e) => setSelectedMember({ ...selectedMember, name: e.target.value })} required />
              </div>
              <div className="mb-3">
                <label htmlFor="email" className="form-label">Email</label>
                <input type="email" className="form-control" id="email" value={selectedMember.email} onChange={(e) => setSelectedMember({ ...selectedMember, email: e.target.value })} required />
              </div>
              <div className="mb-3">
                <label htmlFor="mobileNumber" className="form-label">Mobile Number</label>
                <input type="text" className="form-control" id="mobileNumber" value={selectedMember.mobile_number || ''} onChange={(e) => setSelectedMember({ ...selectedMember, mobile_number: e.target.value })} />
              </div>
              <div className="mb-3">
                <label htmlFor="membershipPlan" className="form-label">Membership Plan</label>
                <select className="form-select" id="membershipPlan" value={selectedMember.membership_plan} onChange={(e) => setSelectedMember({ ...selectedMember, membership_plan: e.target.value })} >
                  <option value="Monthly">Monthly</option>
                  <option value="Quatar">Quatar</option>
                  <option value="Half">Half-Yearly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>
              <div className="mb-3">
                <label htmlFor="planStartDate" className="form-label">Start Date</label>
                <input type="date" className="form-control" id="planStartDate" value={selectedMember.plan_start_date} onChange={(e) => setSelectedMember({ ...selectedMember, plan_start_date: e.target.value })} required />
              </div>
              <div className="mb-3">
                <label htmlFor="paymentStatus" className="form-label">Payment Status</label>
                <select className="form-select" id="paymentStatus" value={selectedMember.payment_status} onChange={(e) => setSelectedMember({ ...selectedMember, payment_status: e.target.value })} >
                  <option value="Paid">Paid</option>
                  <option value="Pending">Pending</option>
                </select>
              </div>
              <div className="mb-3">
                <label htmlFor="personalTrainer" className="form-label">Personal Trainer</label>
                <select className="form-select" id="personalTrainer" value={selectedMember.personal_trainer_id || ''} onChange={(e) => setSelectedMember({ ...selectedMember, personal_trainer_id: e.target.value === '' ? null : parseInt(e.target.value) })} >
                  <option value="">No Personal Trainer</option>
                  {trainers.map(trainer => (
                    <option key={trainer.id} value={trainer.id}>{trainer.name}</option>
                  ))}
                </select>
              </div>
              <div className="mb-3">
                <label htmlFor="workoutPlan" className="form-label">Workout Plan</label>
                <select className="form-select" id="workoutPlan" value={selectedMember.workout_plan_id || ''} onChange={(e) => setSelectedMember({ ...selectedMember, workout_plan_id: e.target.value === '' ? null : parseInt(e.target.value) })} >
                  <option value="">No Workout Plan</option>
                  {workoutTemplates.map(plan => (
                    <option key={plan.template_id} value={plan.template_id}>{plan.template_name}</option>
                  ))}
                </select>
              </div>
              <Button variant="primary" type="submit">
                Save Changes
              </Button>
            </form>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default Members;
// Trigger recompile